import React, { useRef, useMemo, useState, forwardRef, useImperativeHandle, useEffect } from "react";
import JoditEditor from "jodit-react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from 'react-i18next';

const RichTextEditor = forwardRef(({ value, onChange, readOnly = false, height = "250px", placeholder: placeholderProp }, ref) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const editor = useRef(null);
  const editorRefInternal = useRef(null);
  const [content, setContent] = useState(value || "");

  const placeholder = placeholderProp || t('GENERAL.ENTER_DETAILS');

  useEffect(() => {
    if (value !== content) setContent(value || "");
  }, [value]);

  const handleChange = (newContent) => {
    setContent(newContent);
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = newContent;
    const plainText = tempDiv.textContent || tempDiv.innerText || "";
    onChange && onChange({ html: newContent, text: plainText.trim() });
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      setContent("");
      if (editorRefInternal.current) editorRefInternal.current.value = "";
    },
  }));

  const config = useMemo(() => {
    const baseButtons = [
      "bold",
      "italic",
      "underline",
      "ul",
      "ol",
      "|",
      "left",
      "center",
      "right",
      "justify",
      "|",
      "undo",
      "redo",
    ];
    return {
      readonly: readOnly,
      toolbarSticky: false,
      toolbarAdaptive: true,
      toolbarButtonSize: "small",
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      addNewLine: false,
      placeholder,
      buttons: baseButtons,
      buttonsSM: baseButtons,
      buttonsXS: baseButtons,
      height: typeof height === "number" ? height : height,
      minHeight: typeof height === "number" ? height : undefined,
      style: {
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      },
    };
  }, [readOnly, height, theme]);

  return (
    <Box
      sx={{
        width: "100%",
        "& .jodit-container": {
          background: theme.palette.background.paper,
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
          borderRadius: 1,
        },
        "& .jodit-toolbar__box, & .jodit-toolbar-editor-collection, & .jodit-toolbar__box:not(.jodit-status-bar)": {
          background: theme.palette.action.hover,
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        "& .jodit-toolbar-button, & .jodit-toolbar-button__button": { color: theme.palette.text.primary },
        "& .jodit-toolbar-button__button:hover": { background: theme.palette.action.selected },
        "& .jodit-toolbar-button__button.jodit-button_active": {
          background: theme.palette.action.selected,
          color: theme.palette.text.primary,
        },
        "& .jodit-toolbar-button__button[disabled]": { color: theme.palette.text.disabled, opacity: 0.6 },
        "& .jodit-toolbar-button__button svg, & .jodit-toolbar-button__icon, & .jodit-toolbar-button__icon svg": {
          color: theme.palette.text.primary,
          fill: theme.palette.text.primary,
        },
        "& .jodit-toolbar-button__button svg path, & .jodit-toolbar-button__button svg rect, & .jodit-toolbar-button__button svg circle, & .jodit-toolbar-button__button svg line": {
          stroke: theme.palette.text.primary,
          fill: theme.palette.text.primary,
        },
        "& .jodit-wysiwyg": {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          padding: 1.5,
          minHeight: typeof height === "number" ? `${height}px` : height,
        },
        "& .jodit-workplace": { background: theme.palette.background.paper },
        "& .jodit-placeholder": { color: theme.palette.text.disabled },
      }}
    >
      <JoditEditor
        ref={editor}
        value={content}
        config={config}
        onBlur={(newContent, joditInstance) => {
          editorRefInternal.current = joditInstance;
          handleChange(newContent);
        }}
      />
    </Box>
  );
});

export default RichTextEditor;

